import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  ReferenceLine,
} from "recharts";
import {
  Globe, Users, MousePointerClick, FileText, Timer, LogOut, UserPlus, Radio, Search, Eye, Percent,
  ListOrdered, Copy, RefreshCw, CheckCircle2, Circle, TrendingUp,
} from "lucide-react";
import { api } from "./api";
import { Bloc, CarteIcone, Puces, Eyebrow } from "./ui";
import { Encart, Repartition } from "./DashboardPlus";

/* « Le site web » dans le tableau de bord (école, 23.10.2026 : « combien de gens viennent,
   combien de temps ils restent, tout ce qu'on peut avoir, depuis début août »).

   1. En tête, les visites depuis le 1er août en un seul chiffre (école, 24.10.2026 : « on
      combine Google et tes stats ? ») : venues de Google avant le premier jour mesuré par
      le site, toutes les visites ensuite, sans compter deux fois (/stats/site/depuis-aout).
   2. Le détail, mesuré par le site lui-même depuis le 24.10.2026 (sans cookie) :
      /stats/site?jours=7|30|90|365.
   3. Depuis début août, ce qui existe déjà : les formulaires reçus, et Google (Search
      Console) une fois l'accès ouvert : /stats/site/depuis-aout. */

const nb = (n) => new Intl.NumberFormat("fr-CH").format(n || 0);
const jourFr = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const duree = (s) => {
  const t = Math.round(s || 0);
  if (t < 60) return `${t} s`;
  const m = Math.floor(t / 60);
  return t % 60 ? `${m} min ${t % 60} s` : `${m} min`;
};
const variation = (v) => (v === null || v === undefined ? "" : `${v > 0 ? "+" : ""}${v} % vs période d'avant`);
const JOURS = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];
const infobulle = { contentStyle: { borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 } };

const ACCENT = "#65A30D";
const BLEU_GOOGLE = "#93C5FD";
const COULEURS_SOURCES = {
  Google: "#2563EB", Direct: "#64748B", Instagram: "#DB2777", Facebook: "#1D4ED8",
  "Assistants IA": "#7C3AED", "Autres moteurs": "#0891B2", "E-mail": "#EA580C", WhatsApp: "#16A34A",
  TikTok: "#0F172A", "Autres sites": "#CA8A04", LinkedIn: "#0369A1", YouTube: "#DC2626",
};
const DETAIL_SOURCES = {
  Direct: "adresse tapée, favori, lien WhatsApp ou e-mail (ils ne disent pas d'où l'on vient)",
  "Assistants IA": "ChatGPT, Perplexity, Gemini…",
};
const COULEURS_APPAREILS = { mobile: "#DB2777", ordinateur: "#2563EB", tablette: "#CA8A04" };
const NOMS_APPAREILS = { mobile: "Téléphone", ordinateur: "Ordinateur", tablette: "Tablette" };

function useDonnees(chemin) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    let vivant = true;
    setErreur("");
    api(chemin).then((x) => vivant && setD(x)).catch((e) => vivant && setErreur(e.message || "erreur"));
    return () => { vivant = false; };
  }, [chemin]);
  return [d, erreur, setD];
}

const Attente = ({ erreur }) => (erreur
  ? <p className="text-sm" style={{ color: "#B91C1C" }}>Ces chiffres ne sont pas disponibles pour le moment ({erreur}).</p>
  : <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
      {[0, 1, 2, 3].map((k) => <div key={k} className="h-28 rounded-2xl" style={{ background: "#F1F5F9" }} />)}
    </div>);

/* Une liste « nom … chiffre », avec une petite barre proportionnelle. */
const Classement = ({ lignes, testid, vide = "Rien pour l'instant.", couleur = ACCENT, suffixe }) => {
  const max = Math.max(1, ...lignes.map((l) => l.valeur));
  if (!lignes.length) return <p className="text-sm text-[var(--muted)]">{vide}</p>;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {lignes.map((l) => (
        <li key={l.cle || l.nom} className="text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate">{l.nom}</span>
            {l.detail && <span className="text-[11px] text-[var(--muted)] truncate">{l.detail}</span>}
            <span className="ml-auto font-semibold tabular-nums">{nb(l.valeur)}{suffixe || ""}</span>
          </div>
          <div className="h-1 rounded-full mt-1" style={{ background: "#EEF2F6" }}>
            <div className="h-1 rounded-full" style={{ width: `${(100 * l.valeur) / max}%`, background: couleur }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

export function SectionSite() {
  const [jours, setJours] = useState(() => {
    try { return Number(localStorage.getItem("tdb_site_jours")) || 30; } catch { return 30; }
  });
  const choisir = (j) => {
    setJours(j);
    try { localStorage.setItem("tdb_site_jours", String(j)); } catch { /* rien */ }
  };
  const [d, erreur] = useDonnees(`/stats/site?jours=${jours}`);
  const [rafraichir, setRafraichir] = useState(0);
  const [a, erreurA, setA] = useDonnees(`/stats/site/depuis-aout${rafraichir ? `?rafraichir=true&n=${rafraichir}` : ""}`);
  const relire = () => { setA(null); setRafraichir((n) => n + 1); };
  return (
    <Bloc id="tdb-site" titre="Le site web" Icon={Globe} ton="site" testid="section-site"
      aide="Les visites de 1000arts.ch depuis le 1er août : celles venues de Google avant que le site les mesure, puis toutes les visites (sans cookie, sans les applications ni les ordinateurs de l'école).">
      {!a ? <Attente erreur={erreurA} /> : a.visites ? <VisitesDepuisAout v={a.visites} /> : null}
      <div className="pt-2 space-y-4" data-testid="site-detail">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Eyebrow>Le détail des visites</Eyebrow>
            <p className="text-xs text-[var(--muted)] mt-1">
              Mesuré par le site lui-même depuis sa mise en ligne du 24.10.2026 : temps passé, pages, sources, appareils, lieux.
            </p>
          </div>
          <Puces valeur={String(jours)} onChange={(v) => choisir(Number(v))} testid="site-periode"
            options={[7, 30, 90, 365].map((j) => ({ valeur: String(j), label: j === 365 ? "1 an" : `${j} jours`, testid: `site-periode-${j}` }))} />
        </div>
        {!d ? <Attente erreur={erreur} /> : <Visites d={d} />}
      </div>
      <DepuisAout d={a} erreur={erreurA} onRelire={relire} />
    </Bloc>
  );
}

/* Les visites depuis le 1er août, en un seul chiffre : Google seulement avant le premier
   jour mesuré (le seul chiffre qui existe), toutes les visites ensuite. */
function VisitesDepuisAout({ v }) {
  const debut = v.depuis_mesure;
  const lignes = v.semaines.map((s) => ({ ...s, etiquette: jourFr(s.semaine).slice(0, 5) }));
  // La semaine (du lundi) où la mesure commence : la ligne pointillée du graphique.
  const lundiDebut = debut ? ([...lignes].reverse().find((l) => l.semaine <= debut) || {}).etiquette : null;
  const s = (n) => (n > 1 ? "s" : "");
  return (
    <div className="space-y-4" data-testid="site-fusion">
      <div className="grid sm:grid-cols-3 gap-4">
        <CarteIcone titre="Visites depuis le 1er août" valeur={nb(v.total)} Icon={MousePointerClick} teinte="vert" testid="fusion-total"
          detail={debut
            ? `${nb(v.google_avant)} venues de Google avant le ${jourFr(debut)} + ${nb(v.site)} mesurées par le site depuis`
            : `${nb(v.google_avant)} venues de Google`} />
        <CarteIcone titre="Mesurées par le site" valeur={nb(v.site)} Icon={Users} teinte="bleu" testid="fusion-site"
          detail={debut
            ? `toutes les visites depuis le ${jourFr(debut)} (${v.jours_mesures} jour${s(v.jours_mesures)}), Google compris`
            : "la mesure démarre à la première visite"} />
        <CarteIcone titre={`Avant le ${debut ? jourFr(debut) : "début de la mesure"}, en vrai`} Icon={TrendingUp} teinte="ambre" testid="fusion-estimation"
          valeur={v.estimation ? `≈ ${nb(v.estimation.avant)}` : "bientôt"}
          detail={v.estimation
            ? `estimation : ${v.estimation.part_google} % des visites viennent de Google (mesuré par le site)`
            : `Google ne voit qu'une partie des visiteurs : estimation du vrai total dès le ${jourFr(v.estimation_le)}, après 2 semaines de mesure`} />
      </div>
      <Encart titre="Semaine par semaine depuis le 1er août" testid="fusion-semaines"
        aide={debut
          ? `Bleu clair : les visites venues de Google, le seul chiffre connu avant le ${jourFr(debut)}. Vert : toutes les visites (Google, Instagram, adresse tapée…), mesurées par le site depuis ce jour-là. Les barres vertes sont donc plus hautes sans que la fréquentation ait bondi.`
          : "Les visites venues de Google, semaine par semaine."}>
        <div style={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={lignes} margin={{ top: 14, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF2F6" />
              <XAxis dataKey="etiquette" tick={{ fontSize: 10, fill: "#64748B" }} minTickGap={14} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
              <Tooltip {...infobulle} cursor={{ fill: "rgba(101,163,13,0.06)" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="google" name="Venues de Google (seul chiffre connu)" stackId="v" fill={BLEU_GOOGLE} />
              <Bar dataKey="site" name="Toutes les visites (mesurées par le site)" stackId="v" fill={ACCENT} radius={[4, 4, 0, 0]} />
              {lundiDebut && <ReferenceLine x={lundiDebut} stroke="#3F6212" strokeDasharray="4 3"
                label={{ value: "début de la mesure", position: "insideTopRight", fontSize: 10, fill: "#3F6212" }} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Encart>
      {!v.google_branche && (
        <p className="text-xs" style={{ color: "#B45309" }} data-testid="fusion-sans-google">
          Google n'est pas branché : avant la mesure du site, rien n'est compté (voir « Depuis début août » plus bas).
        </p>
      )}
    </div>
  );
}

function Visites({ d }) {
  if (!d.depuis) {
    return (
      <div className="rounded-2xl border p-5 text-sm" style={{ borderColor: "#D9F99D", background: "#F7FEE7" }} data-testid="site-vide">
        <strong>La mesure démarre avec la mise en ligne du site du 24.10.2026.</strong> Les premières visites
        apparaîtront ici quelques minutes après. Avant, aucun outil ne comptait les visites (l'ancien
        compteur appartenait à Emergent) : pour remonter jusqu'à août, voir « Depuis début août » plus bas.
      </div>
    );
  }
  const serie = d.serie.map((j) => ({ ...j, etiquette: jourFr(j.jour).slice(0, 5) }));
  const intervalle = Math.max(0, Math.floor(serie.length / 10) - 1);
  const totalSources = d.sources.reduce((n, s) => n + s.valeur, 0);
  return (
    <>
      <p className="text-xs text-[var(--muted)]" data-testid="site-depuis">
        Du {jourFr(d.du)} au {jourFr(d.au)} · mesure depuis le {jourFr(d.depuis)}
        {d.depuis > d.du ? " (la période commence avant : la comparaison n'est pas encore parlante)" : ""}.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CarteIcone titre="Visiteurs" valeur={nb(d.visiteurs)} Icon={Users} teinte="vert" testid="site-visiteurs"
          detail={[`${d.nouveaux} % de nouveaux`, variation(d.variation.visiteurs)].filter(Boolean).join(" · ")} />
        <CarteIcone titre="Visites" valeur={nb(d.visites)} Icon={MousePointerClick} teinte="bleu" testid="site-visites"
          detail={[`${String(d.pages_par_visite).replace(".", ",")} pages par visite`, variation(d.variation.visites)].filter(Boolean).join(" · ")} />
        <CarteIcone titre="Pages vues" valeur={nb(d.pages_vues)} Icon={FileText} teinte="violet" testid="site-pages-vues"
          detail={variation(d.variation.pages_vues) || "toutes les pages ouvertes"} />
        <CarteIcone titre="Temps sur le site" valeur={duree(d.duree_moyenne)} Icon={Timer} teinte="ambre" testid="site-duree"
          detail={`en moyenne par visite · la moitié des visites durent moins de ${duree(d.duree_mediane)}`} />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <CarteIcone titre="Repartis après une page" valeur={`${d.rebond} %`} Icon={LogOut} teinte="neutre" testid="site-rebond"
          detail="des visites n'ont ouvert qu'une seule page" />
        <CarteIcone titre="Vers l'inscription" valeur={nb(d.vers_inscription)} Icon={UserPlus} teinte="rose" testid="site-inscription"
          detail={`visites qui ont ouvert un formulaire · ${nb(d.formulaires)} formulaire${d.formulaires > 1 ? "s" : ""} reçu${d.formulaires > 1 ? "s" : ""} sur la période`} />
        <CarteIcone titre="En ce moment" valeur={nb(d.en_ligne)} Icon={Radio} teinte="sarcelle" testid="site-en-ligne"
          detail="visites actives ces 5 dernières minutes" />
      </div>

      <Encart titre="Jour par jour" aide="Visiteurs et visites chaque jour ; la ligne : les pages vues." testid="site-graphe">
        <div style={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={serie} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF2F6" />
              <XAxis dataKey="etiquette" tick={{ fontSize: 10, fill: "#64748B" }} interval={intervalle} minTickGap={14} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
              <Tooltip {...infobulle} cursor={{ fill: "rgba(101,163,13,0.06)" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="visiteurs" name="Visiteurs" fill={ACCENT} radius={[4, 4, 0, 0]} />
              <Bar dataKey="visites" name="Visites" fill="#BEF264" radius={[4, 4, 0, 0]} />
              <Line dataKey="pages_vues" name="Pages vues" stroke="#7C3AED" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Encart>

      <div className="grid lg:grid-cols-3 gap-4">
        <Encart titre="Pages les plus vues" aide="Vues, visiteurs différents et temps moyen passé sur la page." className="lg:col-span-2" testid="site-pages">
          {d.pages.length === 0 ? <p className="text-sm text-[var(--muted)]">Rien pour l'instant.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] text-[var(--muted)] text-left">
                    <th className="py-1.5 font-medium">Page</th>
                    <th className="py-1.5 font-medium text-right">Vues</th>
                    <th className="py-1.5 font-medium text-right">Visiteurs</th>
                    <th className="py-1.5 font-medium text-right">Temps moyen</th>
                  </tr>
                </thead>
                <tbody>
                  {d.pages.map((p) => (
                    <tr key={p.chemin} className="border-t" style={{ borderColor: "var(--border-soft)" }}>
                      <td className="py-1.5 pr-3">
                        <span className="block truncate max-w-[260px]">{p.nom}</span>
                        <span className="block text-[11px] text-[var(--muted)]">{p.chemin}</span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-semibold">{nb(p.vues)}</td>
                      <td className="py-1.5 text-right tabular-nums">{nb(p.visiteurs)}</td>
                      <td className="py-1.5 text-right tabular-nums">{p.temps_moyen ? duree(p.temps_moyen) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Encart>
        <Encart titre="Pages d'arrivée" aide="La première page de chaque visite." testid="site-entrees">
          <Classement lignes={d.entrees.map((e) => ({ cle: e.chemin, nom: e.nom, valeur: e.valeur }))} />
        </Encart>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Encart titre="D'où viennent les visiteurs" aide="Pour chaque visite, la page ou l'appli d'où l'on arrive." testid="site-sources">
          <Repartition testid="site-sources-parts" vide="Aucune visite sur la période." parts={d.sources.map((s) => ({
            cle: s.nom, label: s.nom, valeur: s.valeur, couleur: COULEURS_SOURCES[s.nom] || "#94A3B8", detail: DETAIL_SOURCES[s.nom],
          }))} />
          {d.origines.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] font-semibold text-[var(--muted)] mb-1.5">Sites d'origine</div>
              <Classement lignes={d.origines.map((o) => ({ nom: o.nom, valeur: o.valeur }))} couleur="#CA8A04" testid="site-origines" />
            </div>
          )}
          {d.campagnes.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] font-semibold text-[var(--muted)] mb-1.5">Campagnes (liens utm)</div>
              <Classement lignes={d.campagnes} couleur="#EA580C" />
            </div>
          )}
          {totalSources > 0 && <p className="text-[11px] text-[var(--muted)] mt-3">Sur {nb(totalSources)} visites.</p>}
        </Encart>
        <Encart titre="Appareils" testid="site-appareils">
          <Repartition testid="site-appareils-parts" vide="Aucune visite sur la période." parts={d.appareils.map((a) => ({
            cle: a.nom, label: NOMS_APPAREILS[a.nom] || a.nom, valeur: a.valeur, couleur: COULEURS_APPAREILS[a.nom] || "#94A3B8",
          }))} />
          <div className="mt-5">
            <div className="text-[11px] font-semibold text-[var(--muted)] mb-1.5">Systèmes</div>
            <Classement lignes={d.systemes} couleur="#2563EB" testid="site-systemes" />
          </div>
        </Encart>
        <Encart titre="D'où ils se connectent" aide="Lieu approximatif donné par l'hébergeur (jamais l'adresse IP)." testid="site-lieux">
          <Classement lignes={d.pays.map((p) => ({ cle: p.code, nom: p.nom, valeur: p.valeur }))} couleur="#0891B2" testid="site-pays" />
          {d.regions.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] font-semibold text-[var(--muted)] mb-1.5">Cantons (Suisse)</div>
              <Classement lignes={d.regions} couleur="#0891B2" testid="site-cantons" />
            </div>
          )}
          {d.villes.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] font-semibold text-[var(--muted)] mb-1.5">Villes (Suisse)</div>
              <Classement lignes={d.villes} couleur="#0891B2" testid="site-villes" />
            </div>
          )}
        </Encart>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Encart titre="À quelle heure" aide="Début des visites, heure suisse." className="lg:col-span-2" testid="site-heures">
          <div style={{ height: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.heures.map((h) => ({ ...h, etiquette: `${h.heure} h` }))} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEF2F6" />
                <XAxis dataKey="etiquette" tick={{ fontSize: 10, fill: "#64748B" }} interval={2} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <Tooltip {...infobulle} cursor={{ fill: "rgba(101,163,13,0.06)" }} />
                <Bar dataKey="valeur" name="Visites" fill={ACCENT} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Encart>
        <Encart titre="Quel jour" testid="site-jours">
          <Classement lignes={d.jours_semaine.map((j) => ({ cle: j.jour, nom: JOURS[j.jour], valeur: j.valeur }))} />
        </Encart>
      </div>
    </>
  );
}

// ── Depuis début août ───────────────────────────────────────────────────────

const COULEURS_FORMULAIRES = {
  semestrielle: "#2563EB", carte10: "#0891B2", essai: "#65A30D", stage: "#7C3AED", workshop: "#DB2777",
  audition: "#EA580C", spectacle: "#CA8A04", cours_prive: "#0F766E", contact: "#94A3B8",
};

function DepuisAout({ d, erreur, onRelire }) {
  return (
    <div className="pt-2 space-y-4" data-testid="site-depuis-aout">
      <div>
        <Eyebrow>Depuis début août</Eyebrow>
        <p className="text-xs text-[var(--muted)] mt-1">
          Avant la mesure du site, deux sources remontent jusqu'au 1er août : les formulaires reçus, et Google.
        </p>
      </div>
      {!d ? <Attente erreur={erreur} /> : (
        <>
          <Formulaires f={d.formulaires} />
          <Google g={d.google} onRelire={onRelire} />
        </>
      )}
    </div>
  );
}

function Formulaires({ f }) {
  const actives = f.sortes.filter((s) => (f.totaux[s.cle] || 0) > 0);
  const lignes = f.semaines.map((s) => ({ ...s, etiquette: jourFr(s.semaine).slice(0, 5) }));
  const total = Object.values(f.totaux).reduce((n, x) => n + x, 0);
  return (
    <Encart titre="Formulaires reçus par semaine" testid="site-formulaires"
      aide={`${nb(total)} formulaires du site depuis le ${jourFr(f.depuis)} (semaines du lundi au dimanche).`}>
      {total === 0 ? <p className="text-sm text-[var(--muted)]">Aucun formulaire reçu depuis le {jourFr(f.depuis)}.</p> : (
      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={lignes} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#EEF2F6" />
            <XAxis dataKey="etiquette" tick={{ fontSize: 10, fill: "#64748B" }} minTickGap={14} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
            <Tooltip {...infobulle} cursor={{ fill: "rgba(37,99,235,0.05)" }} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            {actives.map((s, i) => (
              <Bar key={s.cle} dataKey={s.cle} name={s.nom} stackId="f" fill={COULEURS_FORMULAIRES[s.cle] || "#94A3B8"}
                radius={i === actives.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      )}
    </Encart>
  );
}

function Google({ g, onRelire }) {
  if (!g.branche) return <GoogleABrancher g={g} onRelire={onRelire} />;
  const semaines = g.semaines.map((s) => ({ ...s, etiquette: jourFr(s.semaine).slice(0, 5) }));
  return (
    <div className="space-y-4" data-testid="site-google">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CarteIcone titre="Visites venues de Google" valeur={nb(g.total.clics)} Icon={MousePointerClick} teinte="bleu" testid="google-clics"
          detail={`clics dans les résultats depuis le ${jourFr(g.du)}`} />
        <CarteIcone titre="Affichages dans Google" valeur={nb(g.total.affichages)} Icon={Eye} teinte="indigo" testid="google-affichages"
          detail="fois où le site est apparu dans les résultats" />
        <CarteIcone titre="Taux de clic" valeur={`${String(g.total.taux).replace(".", ",")} %`} Icon={Percent} teinte="vert" testid="google-taux"
          detail="clics ÷ affichages" />
        <CarteIcone titre="Position moyenne" valeur={String(g.total.position).replace(".", ",")} Icon={ListOrdered} teinte="ambre" testid="google-position"
          detail="dans les résultats (1 = tout en haut)" />
      </div>
      <Encart titre="Google semaine par semaine" testid="google-semaines"
        aide={`Clics et affichages. Google a 2 à 3 jours de retard${g.dernier_jour ? ` (dernier jour connu : ${jourFr(g.dernier_jour)})` : ""}.`}
        droite={<button type="button" onClick={onRelire} className="text-xs inline-flex items-center gap-1 text-[var(--muted)] hover:underline" data-testid="google-actualiser">
          <RefreshCw size={12} /> Actualiser</button>}>
        <div style={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={semaines} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF2F6" />
              <XAxis dataKey="etiquette" tick={{ fontSize: 10, fill: "#64748B" }} minTickGap={14} tickLine={false} axisLine={false} />
              <YAxis yAxisId="c" allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="a" orientation="right" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} />
              <Tooltip {...infobulle} cursor={{ fill: "rgba(37,99,235,0.05)" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="c" dataKey="clics" name="Clics (visites)" fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Line yAxisId="a" dataKey="affichages" name="Affichages" stroke="#94A3B8" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Encart>
      <div className="grid lg:grid-cols-2 gap-4">
        <Encart titre="Ce que les gens cherchent sur Google" aide="Recherches qui ont mené au site, par clics." testid="google-recherches">
          <Classement lignes={g.recherches.map((r) => ({ nom: r.nom, detail: `${nb(r.affichages)} affichages`, valeur: r.clics }))}
            couleur="#2563EB" vide="Google ne donne pas encore de recherches." />
        </Encart>
        <Encart titre="Pages trouvées par Google" aide="Par clics depuis les résultats." testid="google-pages">
          <Classement lignes={g.pages.map((p) => ({ nom: p.nom.replace(/^https?:\/\/(www\.)?1000arts\.ch/, "") || "/", detail: `${nb(p.affichages)} affichages`, valeur: p.clics }))}
            couleur="#2563EB" />
        </Encart>
      </div>
      {g.lu_le && <p className="text-[11px] text-[var(--muted)]">Lu chez Google le {jourFr(g.lu_le)} à {String(g.lu_le).slice(11, 16)} (UTC){g.ancien ? " — Google ne répond pas, chiffres précédents" : ""}.</p>}
    </div>
  );
}

function GoogleABrancher({ g, onRelire }) {
  const [copie, setCopie] = useState(false);
  const copier = () => navigator.clipboard?.writeText(g.compte).then(() => { setCopie(true); setTimeout(() => setCopie(false), 1500); });
  const lienApi = `https://console.cloud.google.com/apis/library/searchconsole.googleapis.com${g.projet ? `?project=${g.projet}` : ""}`;
  const Etape = ({ n, faite, children }) => (
    <li className="flex gap-3">
      <span className="mt-0.5" style={{ color: faite ? "#15803D" : "#94A3B8" }}>{faite ? <CheckCircle2 size={18} /> : <Circle size={18} />}</span>
      <div className="text-sm min-w-0"><strong>{n}.</strong> {children}</div>
    </li>
  );
  return (
    <div className="rounded-2xl border p-5 space-y-3" style={{ borderColor: "#BFDBFE", background: "#EFF6FF" }} data-testid="google-a-brancher">
      <div className="flex items-center gap-2">
        <Search size={18} style={{ color: "#2563EB" }} />
        <strong className="text-sm">Google depuis début août : deux gestes, une seule fois</strong>
      </div>
      <p className="text-xs" style={{ color: "#1E40AF" }}>
        La Search Console garde 16 mois de recherches : clics depuis Google, ce que les gens ont tapé, les pages trouvées.
        La console peut les lire dès que tu lui en donnes l'accès (lecture seule).
        {g.raison ? <> Pour l'instant : <em>{g.raison}</em></> : null}
      </p>
      <ol className="space-y-2.5">
        <Etape n={1} faite={g.etape >= 2}>
          Google Cloud{g.projet ? <> (projet <code>{g.projet}</code>)</> : null} : ouvre{" "}
          <a href={lienApi} target="_blank" rel="noreferrer" className="underline">« Google Search Console API »</a> et clique <strong>Activer</strong>.
        </Etape>
        <Etape n={2} faite={false}>
          <a href="https://search.google.com/search-console/users" target="_blank" rel="noreferrer" className="underline">Search Console</a>{" "}
          → Paramètres → Utilisateurs et autorisations → <strong>Ajouter un utilisateur</strong> : colle cette adresse, autorisation <strong>« Restreinte »</strong>.
          {g.compte ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <code className="px-2 py-1 rounded-lg bg-white border text-xs break-all" style={{ borderColor: "#BFDBFE" }} data-testid="google-compte">{g.compte}</code>
              <button type="button" onClick={copier} className="text-xs inline-flex items-center gap-1 underline" data-testid="google-copier">
                <Copy size={12} /> {copie ? "Copiée" : "Copier"}
              </button>
            </div>
          ) : <span className="block text-xs mt-1" style={{ color: "#B91C1C" }}>Le compte de service n'est pas configuré sur ce serveur.</span>}
        </Etape>
      </ol>
      <button type="button" onClick={onRelire} className="text-xs px-3 py-1.5 rounded-full bg-white border inline-flex items-center gap-1.5"
        style={{ borderColor: "#BFDBFE" }} data-testid="google-verifier">
        <RefreshCw size={12} /> C'est fait, vérifier
      </button>
    </div>
  );
}
